import { BaseIntegration } from '../../core/BaseIntegration';
import type { IntegrationConfig, IntegrationResult, IntegrationError, IntegrationParams } from '../../types';
import { registerIntegration } from '../../registry';

/**
 * DeepAgent integration for AI code generation
 */
export class DeepAgentIntegration extends BaseIntegration {
  private apiUrl: string;
  private apiKey: string;

  constructor(config: IntegrationConfig) {
    super(config);

    this.apiUrl = config.env.DEEPAGENT_API_URL || 'http://localhost:3000';
    this.apiKey = config.env.DEEPAGENT_API_KEY || '';

    this.logger.info('DeepAgent integration initialized', { apiUrl: this.apiUrl });
  }

  async execute(
    action: string,
    params: IntegrationParams,
  ): Promise<IntegrationResult> {
    // Validate params against registry
    const validation = this.validateParams(action, params);
    if (!validation.valid) {
      return {
        success: false,
        error: {
          name: 'IntegrationError',
          message: 'Validation failed',
          code: 'VALIDATION_ERROR',
          details: validation.errors,
        } as IntegrationError,
        metadata: this.createMetadata(action, 0),
      };
    }

    const startTime = Date.now();
    let retries = 0;

    try {
      let data: unknown;

      switch (action) {
        case 'sendMessage':
          data = await this.executeWithRetry(() => this.sendMessage(params));
          break;
        case 'cancelGeneration':
          data = await this.executeWithRetry(() =>
            this.cancelGeneration(params),
          );
          break;
        case 'validateSchema':
          data = await this.executeWithRetry(() => this.validateSchema(params));
          break;
        case 'compileSchema':
          data = await this.executeWithRetry(() => this.compileSchema(params));
          break;
        case 'getThreadHistory':
          data = await this.executeWithRetry(() =>
            this.getThreadHistory(params),
          );
          break;
        default:
          throw new Error(`Unknown action: ${action}`);
      }

      return {
        success: true,
        data,
        metadata: this.createMetadata(action, Date.now() - startTime, retries),
      };
    } catch (error) {
      return this.handleError(action, error);
    }
  }

  private async sendMessage(params: IntegrationParams) {
    const { message, threadId, skill, context } = params;

    this.logger.debug('Sending message to DeepAgent', { threadId: String(threadId ?? ''), skill: String(skill ?? '') });

    const response = await this.request('/api/agent/message', {
      message,
      threadId,
      skill,
      context,
    });

    return response;
  }

  private async cancelGeneration(params: IntegrationParams) {
    const { threadId } = params;

    this.logger.debug('Cancelling generation', { threadId: String(threadId ?? '') });

    const response = await this.request('/api/agent/cancel', {
      threadId,
    });

    return response;
  }

  private async validateSchema(params: IntegrationParams) {
    const { schema } = params;

    this.logger.debug('Validating schema');

    const response = await this.request('/api/schema/validate', {
      schema,
    });

    return response;
  }

  private async compileSchema(params: IntegrationParams) {
    const { schema, shell } = params;

    this.logger.debug('Compiling schema', { shell: String(shell ?? '') });

    const response = await this.request('/api/schema/compile', {
      schema,
      shell,
    });

    return response;
  }

  private async getThreadHistory(params: IntegrationParams) {
    const { threadId } = params;

    this.logger.debug('Getting thread history', { threadId: String(threadId ?? '') });

    const response = await this.request('/api/agent/history', {
      threadId,
    });

    return response;
  }

  private async request(endpoint: string, body: IntegrationParams) {
    const url = `${this.apiUrl}${endpoint}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(this.apiKey ? { Authorization: `Bearer ${this.apiKey}` } : {}),
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(
        `DeepAgent request failed: ${response.status} ${response.statusText}`,
      );
    }

    return await response.json();
  }
}

// Auto-register
registerIntegration('deepagent', DeepAgentIntegration);
